import axios, { AxiosError } from "axios";
import { setError } from "../redux/actions/error";
import { clearShopLogin } from "../redux/actions/shopLogin";
import moment from "moment";

enum LoginErrors {
  UserNotRegistered = "Email address / password combination is incorrect.",
}

// Auth response interface
interface AuthResponse {
  Role: string;
  ShopName?: string;
  MerchantName?: string;
  MerchantLogoUrl?: string;
  IsCollectEnabled: boolean;
  IsCollectOnly: boolean;
  TermsAccepted: boolean;
}

interface AuthStatusResponse {
  Authenticated: boolean;
  AccessTokenValid: boolean;
  AccessTokenExpiresInSeconds: number | null;
  RefreshTokenValid: boolean;
  RefreshTokenExpiresInSeconds: number | null;
}

// Define the store state type
export interface StoreState {
  login: {
    isUserAdmin: boolean;
    // other login state properties
  };
  // other state slices
}

// Token refresh interval in milliseconds (25 minutes)
// Refreshes 5 minutes before the 30-minute access token expires
const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000;

// Add a small random jitter (±30 seconds) to prevent all clients refreshing at once
const jitter = (Math.random() * 60 - 30) * 1000; // ±30 seconds in milliseconds
const refreshInterval = TOKEN_REFRESH_INTERVAL + jitter;

// Variable to store the refresh interval timer
let tokenRefreshTimer: number | null = null;

// Function to start the background token refresh service
export const startTokenRefreshService = (dispatch: Function) => {
  // Clear any existing timer
  if (tokenRefreshTimer !== null) {
    window.clearInterval(tokenRefreshTimer);
  }

  // Set the initial refresh timestamp if not already set
  if (!localStorage.getItem("lastTokenRefresh")) {
    localStorage.setItem("lastTokenRefresh", Date.now().toString());
  }

  // Set up the interval to refresh the token
  tokenRefreshTimer = window.setInterval(async () => {
    try {
      await refreshToken(dispatch);
    } catch (error) {
      // Error in background token refresh
    }
  }, refreshInterval);
};

// Function to stop the background token refresh service
export const stopTokenRefreshService = (): void => {
  if (tokenRefreshTimer !== null) {
    window.clearInterval(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
};

// Login function
export const login = async (
  email: string,
  password: string,
  dispatch: Function,
): Promise<AuthResponse | null> => {
  try {
    const response = await axios.post("/portal/login", {
      Email: email,
      Password: password,
    });

    return response.data.Result;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.data?.Error) {
        error.response?.data.Error === "UserNotRegistered"
          ? dispatch(setError(LoginErrors.UserNotRegistered))
          : dispatch(setError(error.response?.data.Error));
      } else {
        dispatch(setError("Login failed. Please try again."));
      }
    } else {
      dispatch(setError("An unexpected error occurred."));
    }
    return null;
  }
};

// Refresh token function
export const refreshToken = async (dispatch: Function): Promise<boolean> => {
  try {
    // With HttpOnly cookies, we don't need to check if the refresh token exists
    // The server will handle that for us

    const response = await axios.post("/portal/refresh-token", {});

    if (response.status === 200) {
      // Store the time of the successful refresh
      localStorage.setItem("lastTokenRefresh", Date.now().toString());
      return true;
    } else {
      return false;
    }
  } catch (error) {
    // Only clear login state if it's a 401 error (unauthorized)
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      dispatch(clearShopLogin());
    }
    return false;
  }
};

export const checkAuthStatus = async (): Promise<AuthStatusResponse | null> => {
  try {
    const response = await axios.get<{ Result: AuthStatusResponse }>(
      "/portal/check-auth-status",
    );
    return response.data.Result;
  } catch {
    return null;
  }
};

// Logout function
export const logout = async (dispatch: Function): Promise<void> => {
  try {
    await axios.post("/portal/logout", {});
  } catch (error) {
    // Ignore errors during logout
  }

  // Stop the token refresh service
  stopTokenRefreshService();

  // Clear local storage
  localStorage.removeItem("lastTokenRefresh");

  // Clear Redux state
  dispatch(clearShopLogin());
};

// Logout from all devices
export const logoutAll = async (dispatch: Function): Promise<void> => {
  try {
    await axios.post("/portal/logout-all", {});
  } catch (error) {
    // Ignore errors during logout
  }

  // Stop the token refresh service
  stopTokenRefreshService();

  // Clear local storage
  localStorage.removeItem("lastTokenRefresh");

  // Clear Redux state
  dispatch(clearShopLogin());
};

// Modify the setupAuthInterceptor function to focus only on handling 401 errors
export const setupAuthInterceptor = (dispatch: Function) => {

  // For POST requests to /check-new/ endpoints, proactively refresh the token
  // if the last refresh was more than 20 minutes ago
  axios.interceptors.request.use(async (config) => {
    if (
      config.method?.toLowerCase() === "post" &&
      config.url?.includes("/check-new/")
    ) {
      const lastRefresh = localStorage.getItem("lastTokenRefresh");
      const tokenIsStale =
        !lastRefresh ||
        moment(parseInt(lastRefresh, 10)).isBefore(moment().subtract(20, "minutes"));
      if (tokenIsStale) {
        await refreshToken(dispatch);
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Only proceed if there's a response object
      if (!error.response) {
        return Promise.reject(error);
      }

      const originalRequest = error.config;

      // Make sure originalRequest is defined
      if (!originalRequest) {
        return Promise.reject(error);
      }

      // If the error is 401 and we haven't tried to refresh the token yet
      if (
        (error.response.status === 401 || error.response.status === 400) &&
        !originalRequest._retry &&
        originalRequest.url &&
        !originalRequest.url.includes("/portal/login") &&
        !originalRequest.url.includes("/portal/refresh-token")
      ) {
        originalRequest._retry = true;

        try {
          // Try to refresh the token
          const refreshed = await refreshToken(dispatch);
          if (refreshed) {
            // Retry the original request
            return axios(originalRequest);
          } else {
            // If refresh failed, stop the token refresh service
            stopTokenRefreshService();
            // Clear login state
            dispatch(clearShopLogin());
          }
        } catch (refreshError) {
          // If there was an error, stop the token refresh service
          stopTokenRefreshService();
          // Clear login state
          dispatch(clearShopLogin());
        }
      }

      return Promise.reject(error);
    },
  );
};
